import { ITableCol } from '@data/interfaces/itable.interface';

/**
 * Constantes para el módulo de Estrategias del sistema Dripping
 * Define columnas de tabla, acciones disponibles, opciones y configuraciones
 */

// ============= OPCIONES DE ESTADO =============
export const STRATEGY_STATUS_OPTIONS = [
  { value: 'true', label: 'Activa' },
  { value: 'false', label: 'Inactiva' }
];

export const STRATEGY_STATUS_ENUM = {
  'true': 'Activa',
  'false': 'Inactiva'
};

export const STRATEGY_STATUS_COLOR_ENUM = {
  'true': 'success',
  'false': 'danger'
};

// ============= OPCIONES DE AUTO-EJECUCIÓN =============
export const AUTO_EXECUTE_OPTIONS = [
  { value: 'true', label: 'Automática' },
  { value: 'false', label: 'Manual' }
];

export const AUTO_EXECUTE_ENUM = {
  'true': 'Automática',
  'false': 'Manual'
};

export const AUTO_EXECUTE_COLOR_ENUM = {
  'true': 'success',
  'false': 'warning'
};

// ============= COLUMNAS DE LA TABLA =============
export const STRATEGY_COLUMNS: ITableCol[] = [
  { 
    type: 'text', 
    field: '_id', 
    header: 'ID', 
    showFilter: false, 
    dataType: 'link', 
    linkField: '_id', 
    rootLink: 'dripping/strategies' 
  },
  { 
    type: 'text', 
    field: 'name', 
    header: 'Nombre', 
    showFilter: true 
  },
  { 
    type: 'text', 
    field: 'scenarioDisplay', 
    header: 'Escenario', 
    showFilter: true 
  },
  { 
    type: 'text', 
    field: 'activeChannels', 
    header: 'Canales Activos', 
    showFilter: false 
  },
  { 
    type: 'enum', 
    field: 'autoExecuteStatus', 
    header: 'Auto-Ejecución', 
    showFilter: true, 
    dataType: 'enum', 
    enum: AUTO_EXECUTE_ENUM, 
    enumColor: AUTO_EXECUTE_COLOR_ENUM, 
    options: AUTO_EXECUTE_OPTIONS 
  },
  { 
    type: 'enum', 
    field: 'activeStatus', 
    header: 'Estado', 
    showFilter: true, 
    dataType: 'enum', 
    enum: STRATEGY_STATUS_ENUM, 
    enumColor: STRATEGY_STATUS_COLOR_ENUM, 
    options: STRATEGY_STATUS_OPTIONS 
  }
];

// ============= ACCIONES DE LA TABLA =============
export const STRATEGY_ACTIONS = [
  {
    title: 'Activar',
    icon: 'fa-solid fa-check-circle',
    action: 'ACTIVATE',
    color: 'success',
    permission: ['dripping', 'update'] as [string, string],
    rule: { field: 'active', value: false, type: 'equals' }
  },
  {
    title: 'Desactivar',
    icon: 'fa-solid fa-ban',
    action: 'DEACTIVATE',
    color: 'warning',
    permission: ['dripping', 'update'] as [string, string],
    rule: { field: 'active', value: true, type: 'equals' }
  },
  {
    title: 'Eliminar',
    icon: 'fa-solid fa-trash',
    action: 'DELETE',
    color: 'danger',
    permission: ['dripping', 'delete'] as [string, string]
  }
];

// ============= OPCIONES DE ESCENARIOS =============
export const SCENARIO_OPTIONS = [
  { label: 'Antes del Vencimiento', value: 'PRE_DUE_DATE' },
  { label: 'Día del Vencimiento', value: 'DUE_DATE' },
  { label: 'Después del Vencimiento (Cobranza)', value: 'POST_DUE_DATE' }
];

// ============= LABELS DINÁMICOS PARA DÍAS SEGÚN SCENARIO =============
export const DAYS_RANGE_LABELS = {
  PRE_DUE_DATE: {
    title: 'Días antes del vencimiento',
    minLabel: 'Días antes',
    maxLabel: '',
    // PRE_DUE_DATE: Solo un día específico (recordatorio, no insistente)
    preview: (min: number, max: number | null) => 
      `${min} días antes del vencimiento`
  },
  DUE_DATE: {
    title: 'Día del vencimiento',
    minLabel: '',
    maxLabel: '',
    preview: () => 'Exactamente el día del vencimiento'
  },
  POST_DUE_DATE: {
    title: 'Días de atraso',
    minLabel: 'Desde (días de atraso)',
    maxLabel: 'Hasta (días de atraso)',
    // POST_DUE_DATE: Rango de días (cobranza)
    // Si max es null, undefined o 0 = sin límite
    preview: (min: number, max: number | null) => {
      const noLimit = max === null || max === undefined || max === 0;
      
      if (noLimit) {
        return `${min} días de atraso en adelante`;
      }
      if (min === max) {
        return `${min} días de atraso`;
      }
      return `${min} a ${max} días de atraso`;
    }
  }
};

// ============= DELAYS PREDEFINIDOS =============
export const DEFAULT_DELAYS = [30, 60, 120, 240];

// ============= ICONOS DE CANALES =============
export const CHANNEL_ICONS = {
  email: 'fa-solid fa-envelope',
  sms: 'fa-solid fa-comment-sms',
  whatsapp: 'fa-brands fa-whatsapp',
  call: 'fa-solid fa-phone'
};

// ============= CONFIGURACIÓN DE FRECUENCIA =============
export const FREQUENCY_CONFIG = {
  /** Valor mínimo de frecuencia */
  MIN_FREQUENCY: 1,
  /** Valor máximo de frecuencia */
  MAX_FREQUENCY: 30,
  /** Intervalo mínimo entre contactos (minutos) */
  MIN_INTERVAL: 5,
  /** Intervalo por defecto (minutos) */
  DEFAULT_INTERVAL: 60
};
